<script setup lang="ts">
import { computed } from 'vue';
import { QLinearProgress } from 'quasar';

import { constant } from '@/constant';
import { formatMoney, getLabel } from '@/utils/data-display-utils';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  totalBudget: number;
  remainingBudget: number;
  dotVotingMeasurementType: number;
  languageEnabled?: Locales[];
  isSticky?: boolean;
}>();

// COMPUTED
const remainingBudgetPercentage = computed(() => {
  const total = Number(props.totalBudget);
  const remaining = Number(props.remainingBudget);
  if (!Number.isFinite(total) || total <= 0) return 0;
  const ratio = remaining / total;
  return Math.min(Math.max(ratio, 0), 1);
});

const titleLabel = computed(() =>
  props.dotVotingMeasurementType === constant.Form.DotVoting.MeasurementType.Money
    ? getLabel('form.dot-voting.remaining-budget', props.languageEnabled)
    : getLabel('form.dot-voting.remaining-points', props.languageEnabled),
);

const compactRemainingLabel = computed(() => getLabel('form.dot-voting.available', props.languageEnabled));
const maximumLabel = computed(() => getLabel('form.dot-voting.maximum', props.languageEnabled));

// FUNCTIONS
const formatValue = (value: number) => {
  if (props.dotVotingMeasurementType === constant.Form.DotVoting.MeasurementType.Money) {
    return formatMoney(value, props.languageEnabled);
  } else {
    return `${value} ${getLabel('form.dot-voting.points-abbreviation', props.languageEnabled)}`;
  }
};
</script>
<template>
  <div class="dot-voting-status" :class="{ 'dot-voting-status--sticky': props.isSticky }">
    <div v-if="!props.isSticky" class="dot-voting-status__regular">
      <div class="dot-voting-status__title">
        {{ titleLabel }}
      </div>
      <div class="dot-voting-status__amount">
        {{ formatValue(props.remainingBudget) }}
      </div>
    </div>
    <div v-else class="dot-voting-status__compact">
      <div class="dot-voting-status__compact-line">
        <span class="dot-voting-status__compact-value">
          {{ formatValue(props.remainingBudget) }}
        </span>
        <span class="dot-voting-status__compact-label">
          {{ compactRemainingLabel }}
        </span>
        <span class="dot-voting-status__compact-separator"> / </span>
        <span class="dot-voting-status__compact-value">
          {{ formatValue(props.totalBudget) }}
        </span>
      </div>
    </div>
    <div class="dot-voting-status__slider">
      <q-linear-progress rounded :value="1 - remainingBudgetPercentage" color="primary" size="10px" />
    </div>
    <div v-if="!props.isSticky" class="dot-voting-status__maximum">
      {{ maximumLabel }}: {{ formatValue(props.totalBudget) }}
    </div>
  </div>
</template>
<style lang="scss">
.dot-voting-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-xs;
  border: solid 1px $color-neutral-grey-300;
  border-radius: 6px;
  padding: $space-lg $space-3xl;
  color: $color-primary-darken;
  font-size: $body-text;
  transition:
    padding 0.3s ease,
    gap 0.3s ease,
    box-shadow 0.3s ease,
    border-color 0.3s ease;

  &__regular {
    display: inherit;
    flex-direction: inherit;
    align-items: inherit;
    gap: $space-xxs;
    width: 100%;
  }

  &__title {
    font-weight: 400;
  }

  &__amount {
    font-weight: bold;
  }

  &__slider {
    margin-top: $space-sm;
    width: 100%;
  }

  &__maximum {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    font-size: $xsmall-text;
    color: $color-neutral-black-text;
  }

  &__compact {
    display: flex;
    width: 100%;
    justify-content: center;
  }

  &__compact-line {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: $space-xxs;
    width: 100%;
    justify-content: center;
    font-size: $small-text;
  }

  &__compact-value {
    font-weight: bold;
    color: $color-primary-darken;
  }

  &__compact-label {
    color: $color-neutral-black-text;
    font-size: $xsmall-text;
  }

  &__compact-separator {
    color: $color-neutral-grey-400;
  }

  &--sticky {
    align-items: stretch;
    gap: $space-xxs;
    padding: $space-sm $space-lg;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    border-color: $color-neutral-grey-400;

    .dot-voting-status__slider {
      margin-top: 0;
    }
  }
}
@media screen and (max-width: $breakpoint-lg) {
  .dot-voting-status {
    padding: $space-lg $space-2xl;

    &--sticky {
      padding: $space-sm $space-lg;
    }
  }
}
@media screen and (max-width: $breakpoint-md) {
  .dot-voting-status {
    padding: $space-md $space-xl;

    &--sticky {
      padding: $space-sm $space-lg;
    }
  }
}
@media screen and (max-width: $breakpoint-sm) {
  .dot-voting-status {
    padding: $space-md $space-lg;

    &--sticky {
      padding: $space-sm $space-md;
    }
  }
}
</style>
